import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  Bell, 
  CheckSquare, 
  MessageSquare, 
  Mail, 
  Calendar, 
  FolderOpen, 
  FileText,
  TrendingUp,
  Megaphone,
  X,
  ChevronRight
} from 'lucide-react';
import { Shield as ShieldIcon, Contact as ContactIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { LoginSummary } from '@/hooks/useLoginSummary';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';

interface LoginSummaryDialogProps {
  open: boolean;
  onClose: () => void;
  summary: LoginSummary | null;
}

export const LoginSummaryDialog = ({ open, onClose, summary }: LoginSummaryDialogProps) => {
  const navigate = useNavigate();

  if (!summary) {
    return (
      <Dialog open={false} onOpenChange={() => {}}>
        <DialogContent><DialogHeader><DialogTitle className="sr-only">Leer</DialogTitle></DialogHeader></DialogContent>
      </Dialog>
    );
  }

  const truncate = (text: string | null, maxLength: number = 50) => {
    if (!text) return '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  const handleTaskClick = (taskId: string) => {
    onClose();
    navigate(`/dashboard/aufgaben?task=${taskId}`);
  };

  const handleTaskNoteClick = (taskId: string) => {
    onClose();
    navigate(`/dashboard/aufgaben?task=${taskId}`);
  };

  const handleChatClick = () => {
    onClose();
    navigate('/dashboard/chat');
  };

  const handleCalendarClick = (eventId: string) => {
    onClose();
    navigate(`/dashboard/kalender`);
  };

  const handleProjectClick = (projectId: string) => {
    onClose();
    navigate(`/dashboard/projekte/${projectId}`);
  };

  const handleFileClick = () => {
    onClose();
    navigate('/dashboard/daten');
  };

  const handleBookingClick = () => {
    onClose();
    navigate('/dashboard/finanzen');
  };

  const handleCampaignClick = () => {
    onClose();
    navigate('/dashboard/marketing');
  };

  const handleInsuranceClick = () => {
    onClose();
    navigate('/dashboard/versicherungen');
  };

  const handleContactClick = () => {
    onClose();
    navigate('/dashboard/kontakte');
  };

  const totalItems = 
    summary.tasks.length +
    summary.taskNotes.length +
    summary.chatMessages.length +
    summary.calendarEvents.length +
    summary.projects.length +
    summary.files.length +
    summary.bookings.length +
    summary.campaigns.length +
    (summary.insurances?.length || 0) +
    (summary.contacts?.length || 0);

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent 
        className="p-0 flex flex-col overflow-hidden border border-transparent bg-card/95 backdrop-blur-xl dialog-gradient-border"
        style={{ width: 520, maxWidth: '95vw', maxHeight: '85vh' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border/50 shrink-0 bg-gradient-to-r from-cyan-500/10 via-blue-500/10 to-purple-600/10">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-gradient-to-br from-cyan-500/20 via-blue-500/20 to-purple-600/20 border border-cyan-500/30">
              <Bell className="h-5 w-5 text-cyan-400" />
            </div>
            <div>
              <DialogHeader className="p-0 space-y-0">
                <DialogTitle className="text-lg font-semibold">Willkommen zurück!</DialogTitle>
                <DialogDescription className="text-sm text-muted-foreground">
                  {totalItems} {totalItems === 1 ? 'Neuigkeit' : 'Neuigkeiten'} seit Ihrem letzten Besuch
                </DialogDescription>
              </DialogHeader>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} className="h-8 w-8">
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Content */}
        <ScrollArea className="flex-1 min-h-0">
          <div className="p-4 space-y-4">
            
            {/* Tasks */}
            {summary.tasks.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                  <CheckSquare className="h-4 w-4 text-blue-500" />
                  <span>{summary.tasks.length} neue {summary.tasks.length === 1 ? 'Aufgabe' : 'Aufgaben'}</span>
                </div>
                <div className="ml-6 space-y-1">
                  {summary.tasks.map(task => (
                    <button
                      key={task.id}
                      onClick={() => handleTaskClick(task.id)}
                      className="w-full text-left p-2 rounded-md hover:bg-accent transition-colors group"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-foreground truncate">{task.title}</p>
                          {task.description && (
                            <p className="text-xs text-muted-foreground truncate">{truncate(task.description, 60)}</p>
                          )}
                        </div>
                        <ChevronRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity shrink-0 ml-2" />
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Task Notes */}
            {summary.taskNotes.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                  <MessageSquare className="h-4 w-4 text-purple-500" />
                  <span>{summary.taskNotes.length} neue {summary.taskNotes.length === 1 ? 'Bemerkung' : 'Bemerkungen'}</span>
                </div>
                <div className="ml-6 space-y-1">
                  {summary.taskNotes.map(note => (
                    <button
                      key={note.id}
                      onClick={() => handleTaskNoteClick(note.task_id)}
                      className="w-full text-left p-2 rounded-md hover:bg-accent transition-colors group"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-foreground truncate">Zu: {note.task_title}</p>
                          <p className="text-xs text-muted-foreground truncate">{truncate(note.content, 60)}</p>
                        </div>
                        <ChevronRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity shrink-0 ml-2" />
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Chat Messages */}
            {summary.chatMessages.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                  <Mail className="h-4 w-4 text-green-500" />
                  <span>{summary.chatMessages.length} neue {summary.chatMessages.length === 1 ? 'Chat-Nachricht' : 'Chat-Nachrichten'}</span>
                </div>
                <div className="ml-6 space-y-1">
                  {summary.chatMessages.map(msg => (
                    <button
                      key={msg.id}
                      onClick={handleChatClick}
                      className="w-full text-left p-2 rounded-md hover:bg-accent transition-colors group"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-foreground truncate">Von: {msg.sender_name}</p>
                          <p className="text-xs text-muted-foreground truncate">{truncate(msg.content, 60)}</p>
                        </div>
                        <ChevronRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity shrink-0 ml-2" />
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Calendar Events */}
            {summary.calendarEvents.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                  <Calendar className="h-4 w-4 text-cyan-500" />
                  <span>{summary.calendarEvents.length} neue {summary.calendarEvents.length === 1 ? 'Kalendereintrag' : 'Kalendereinträge'}</span>
                </div>
                <div className="ml-6 space-y-1">
                  {summary.calendarEvents.map(event => (
                    <button
                      key={event.id}
                      onClick={() => handleCalendarClick(event.id)}
                      className="w-full text-left p-2 rounded-md hover:bg-accent transition-colors group"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-foreground truncate">{event.title}</p>
                          <p className="text-xs text-muted-foreground">
                            {format(new Date(event.start_date), 'dd. MMM yyyy, HH:mm', { locale: de })}
                          </p>
                        </div>
                        <ChevronRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity shrink-0 ml-2" />
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Projects */}
            {summary.projects.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                  <FolderOpen className="h-4 w-4 text-orange-500" />
                  <span>{summary.projects.length} neue {summary.projects.length === 1 ? 'Projekt' : 'Projekte'}</span>
                </div>
                <div className="ml-6 space-y-1">
                  {summary.projects.map(project => (
                    <button
                      key={project.id}
                      onClick={() => handleProjectClick(project.id)}
                      className="w-full text-left p-2 rounded-md hover:bg-accent transition-colors group"
                    >
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-medium text-foreground truncate flex-1">{project.name}</p>
                        <ChevronRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity shrink-0 ml-2" />
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Files */}
            {summary.files.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                  <FileText className="h-4 w-4 text-amber-500" />
                  <span>{summary.files.length} neue {summary.files.length === 1 ? 'Datei' : 'Dateien'}</span>
                </div>
                <div className="ml-6 space-y-1">
                  {summary.files.map(file => (
                    <button
                      key={file.id}
                      onClick={handleFileClick}
                      className="w-full text-left p-2 rounded-md hover:bg-accent transition-colors group"
                    >
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-medium text-foreground truncate flex-1">{file.name}</p>
                        <ChevronRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity shrink-0 ml-2" />
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Bookings (Finanzen) */}
            {summary.bookings.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                  <TrendingUp className="h-4 w-4 text-emerald-500" />
                  <span>{summary.bookings.length} neue {summary.bookings.length === 1 ? 'Buchung' : 'Buchungen'}</span>
                </div>
                <div className="ml-6 space-y-1">
                  {summary.bookings.map(booking => (
                    <button
                      key={booking.id}
                      onClick={handleBookingClick}
                      className="w-full text-left p-2 rounded-md hover:bg-accent transition-colors group"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-foreground truncate">{booking.description}</p>
                          <p className={`text-xs ${booking.type === 'einnahme' ? 'text-emerald-500' : 'text-red-500'}`}>
                            {booking.type === 'einnahme' ? '+' : '-'}{booking.amount.toLocaleString('de-DE')} €
                          </p>
                        </div>
                        <ChevronRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity shrink-0 ml-2" />
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Marketing Campaigns */}
            {summary.campaigns.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                  <Megaphone className="h-4 w-4 text-pink-500" />
                  <span>{summary.campaigns.length} neue {summary.campaigns.length === 1 ? 'Kampagne' : 'Kampagnen'}</span>
                </div>
                <div className="ml-6 space-y-1">
                  {summary.campaigns.map(campaign => (
                    <button
                      key={campaign.id}
                      onClick={handleCampaignClick}
                      className="w-full text-left p-2 rounded-md hover:bg-accent transition-colors group"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-foreground truncate">{campaign.name}</p>
                          <p className="text-xs text-muted-foreground">{campaign.campaign_type}</p>
                        </div>
                        <ChevronRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity shrink-0 ml-2" />
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </ScrollArea>

            {/* Insurances */}
            {summary.insurances?.length > 0 && (
              <div className="space-y-2 px-4 pb-2">
                <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                  <ShieldIcon className="h-4 w-4 text-indigo-500" />
                  <span>{summary.insurances.length} neue {summary.insurances.length === 1 ? 'Versicherung' : 'Versicherungen'}</span>
                </div>
                <div className="ml-6 space-y-1">
                  {summary.insurances.map(ins => (
                    <button key={ins.id} onClick={handleInsuranceClick} className="w-full text-left p-2 rounded-md hover:bg-accent transition-colors group">
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-foreground truncate">{ins.insurance_type}</p>
                          <p className="text-xs text-muted-foreground">{ins.provider}</p>
                        </div>
                        <ChevronRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity shrink-0 ml-2" />
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Contacts */}
            {summary.contacts?.length > 0 && (
              <div className="space-y-2 px-4 pb-4">
                <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                  <ContactIcon className="h-4 w-4 text-teal-500" />
                  <span>{summary.contacts.length} neue {summary.contacts.length === 1 ? 'Kontakt' : 'Kontakte'}</span>
                </div>
                <div className="ml-6 space-y-1">
                  {summary.contacts.map(contact => (
                    <button key={contact.id} onClick={handleContactClick} className="w-full text-left p-2 rounded-md hover:bg-accent transition-colors group">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-medium text-foreground truncate flex-1">
                          {[contact.first_name, contact.last_name].filter(Boolean).join(' ') || contact.company_name || 'Unbekannt'}
                        </p>
                        <ChevronRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity shrink-0 ml-2" />
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

        {/* Footer */}
        <div className="flex justify-center px-6 py-4 border-t border-border/50 shrink-0 bg-secondary/20">
          <Button onClick={onClose} className="min-w-[140px] bg-gradient-to-r from-cyan-500 via-blue-500 to-purple-600 hover:from-cyan-400 hover:via-blue-400 hover:to-purple-500 text-white border-0">
            Alles klar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
